import { useCallback, useEffect, useState } from "react";
import { isStudentSidebarOverlayViewport } from "../utils/studentSidebarOverlay";

/**
 * Keeps the student course menu open on desktop and overlay-only on tablet/mobile.
 * @param {string} locationKey
 * @returns {{
 *   sidebarVisible: boolean,
 *   overlayMode: boolean,
 *   toggleSidebar: () => void,
 *   closeSidebar: () => void,
 * }}
 */
export function useStudentSidebarVisibility(locationKey) {
  const [overlayMode, setOverlayMode] = useState(
    () => isStudentSidebarOverlayViewport(window.innerWidth),
  );
  const [sidebarVisible, setSidebarVisible] = useState(
    () => !isStudentSidebarOverlayViewport(window.innerWidth),
  );

  useEffect(() => {
    const handleResize = () => {
      const overlay = isStudentSidebarOverlayViewport(window.innerWidth);
      setOverlayMode(overlay);
      setSidebarVisible(!overlay);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isStudentSidebarOverlayViewport(window.innerWidth)) {
      setSidebarVisible(false);
    }
  }, [locationKey]);

  useEffect(() => {
    if (!sidebarVisible || !overlayMode) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      if (
        target.closest(".side-sidebar") ||
        target.closest(".side-hamburger") ||
        target.closest(".side-user-menu")
      ) {
        return;
      }
      setSidebarVisible(false);
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setSidebarVisible(false);
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [overlayMode, sidebarVisible]);

  const toggleSidebar = useCallback(() => {
    setSidebarVisible((current) => !current);
  }, []);

  const closeSidebar = useCallback(() => {
    setSidebarVisible(false);
  }, []);

  return {
    sidebarVisible,
    overlayMode,
    toggleSidebar,
    closeSidebar,
  };
}
